import { CSSProperties, useMemo, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { ChartContainer } from '@Components/ChartContainer';
import { Design } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { BPMTable, Header } from '@Components/StickyTable';
import { makeSteppedHeatMap, SequentialGreenRamp } from '@src/utils/colors';
import { download } from '@src/utils/download-utils';
import { MISSING_METADATA_NOTE } from '../audience-penetration-constants';
import { AudiencePenetrationRowWithMetrics } from '../utils/filter';
import { formatCount, formatPct } from '../utils/format';

interface AudiencePenetrationTableProps {
  rows: AudiencePenetrationRowWithMetrics[];
  isBusy: boolean;
}

const numericCellStyle = (backgroundColor?: string): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  height: '100%',
  padding: '0 12px',
  boxSizing: 'border-box',
  backgroundColor
});

export const AudiencePenetrationTable = ({ rows, isBusy }: AudiencePenetrationTableProps) => {
  const [csvContents, setCsvContents] = useState('');
  const heatMap = useMemo(
    () => makeSteppedHeatMap({ min: 0, max: 100, stops: SequentialGreenRamp }),
    []
  );

  const pctBackground = (value: number | null) =>
    value === null ? undefined : heatMap(value * 100);

  const headers: Header[] = [
    {
      name: 'behaviorSegmentName',
      label: 'Segment Name',
      flex: true,
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.behaviorSegmentName,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span className='cell-text'>{row.behaviorSegmentName}</span>
      )
    },
    {
      name: 'behaviorSegmentId',
      label: 'Segment ID',
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.behaviorSegmentId,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span style={numericCellStyle()}>{row.behaviorSegmentId}</span>
      )
    },
    {
      name: 'segmentType',
      label: 'Segment Type',
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.segmentType,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span className='cell-text'>{row.segmentType}</span>
      )
    },
    {
      name: 'segmentUserCount',
      label: 'Total Users',
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.segmentUserCount ?? 0,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span style={numericCellStyle()}>{formatCount(row.segmentUserCount)}</span>
      )
    },
    {
      name: 'reach',
      label: 'Reach',
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.reach,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span style={numericCellStyle()}>{formatCount(row.reach)}</span>
      )
    },
    {
      name: 'audienceReachPct',
      label: 'Audience Reach %',
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.audienceReachPct ?? 0,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span className='numeric-cell'>
          <span
            className='heat-chip'
            style={{ backgroundColor: pctBackground(row.audienceReachPct) }}
          >
            {formatPct(row.audienceReachPct)}
          </span>
        </span>
      )
    },
    {
      name: 'convertedUsers',
      label: 'Converted Users',
      nonInteractive: true,
      sortPriority: 1,
      sortAscending: false,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.convertedUsers,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span style={numericCellStyle()}>{formatCount(row.convertedUsers)}</span>
      )
    },
    {
      name: 'conversionRatePct',
      label: 'Conversion Rate %',
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.conversionRatePct ?? 0,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span className='numeric-cell'>
          <span
            className='heat-chip'
            style={{ backgroundColor: pctBackground(row.conversionRatePct) }}
          >
            {formatPct(row.conversionRatePct)}
          </span>
        </span>
      )
    },
    {
      name: 'audienceConversionPct',
      label: 'Audience Conversion %',
      nonInteractive: true,
      contentGetter: (row: AudiencePenetrationRowWithMetrics) => row.audienceConversionPct ?? 0,
      renderer: (row: AudiencePenetrationRowWithMetrics) => (
        <span className='numeric-cell'>
          <span
            className='heat-chip'
            style={{ backgroundColor: pctBackground(row.audienceConversionPct) }}
          >
            {formatPct(row.audienceConversionPct)}
          </span>
        </span>
      )
    }
  ];

  return (
    <ChartContainer
      className='audience-penetration-table'
      title='Segment Details'
      subHeader={MISSING_METADATA_NOTE}
      isLoading={isBusy}
      rightActions={
        <IconButton
          design={Design.NEUTRAL}
          onClick={() => download(csvContents, 'audience-penetration.csv', 'text/csv')}
        >
          <MdOutlineFileDownload />
        </IconButton>
      }
    >
      <div className='audience-penetration-table-inner'>
        <BPMTable
          data={rows}
          headers={headers}
          alternateColors
          filterBar={false}
          autoHeight
          onCsvChange={setCsvContents}
        />
      </div>
    </ChartContainer>
  );
};
